// ════════════════════════════════════════════════════════════════════════════
//  Faturas de Contas a Receber.
//
//  A fatura é um DOCUMENTO DE COBRANÇA: agrupa parcelas já existentes de um
//  mesmo cliente numa data de vencimento única, para mandar uma cobrança só.
//  A DÍVIDA CONTINUA NOS TÍTULOS — por isso a fatura não entra no "a receber"
//  (senão o total dobraria) e receber a fatura baixa as parcelas agrupadas.
//
//  Duas abas: "Gerar" (candidatas por cliente, seleção e geração em lote) e
//  "Faturas" (as já geradas, com PDF, recebimento e estorno).
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from "react";
import {
  Search, X, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon,
  FileText, Printer, CheckCheck, RotateCcw, Eye, AlertCircle, Layers,
} from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useCandidatasFatura, useFaturas, useFaturaDetalhe, useFaturasMutations,
} from "@/features/financeiro/hooks/useFaturas";
import { DeleteConfirmDialog } from "@/features/financeiro/components/DeleteConfirmDialog";
import type { FiltroFaturas, OrdenarPorFaturas } from "@/features/financeiro/types";
import { PeriodoFiltro, usePeriodo } from "@/components/listas/PeriodoFiltro";
import { CabecalhoOrdenavel, useOrdenacao } from "@/components/listas/CabecalhoOrdenavel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmt = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString("pt-BR") : "—";

const situacaoCor: Record<string, string> = {
  Recebida:  "bg-green-500/10 text-green-500 hover:bg-green-500/20",
  Parcial:   "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20",
  Aberta:    "bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20",
  Vencida:   "bg-red-500/10 text-red-500 hover:bg-red-500/20",
  Cancelada: "bg-muted text-muted-foreground hover:bg-muted",
};

const hojeIso = () => new Date().toISOString().slice(0, 10);

const Faturas = () => {
  const { temPermissao } = usePermissoes();
  const podeIncluir = temPermissao(FORMULARIOS.CONTAS_RECEBER, "incluir");
  const podeEditar  = temPermissao(FORMULARIOS.CONTAS_RECEBER, "editar");
  const podeExcluir = temPermissao(FORMULARIOS.CONTAS_RECEBER, "excluir");

  // ── Aba "Gerar" ──────────────────────────────────────────────────────────
  const [buscaCand, setBuscaCand]       = useState("");
  const [vencFatura, setVencFatura]     = useState("");
  // Parcelas marcadas, por cliente. A fatura é sempre POR CLIENTE — misturar
  // clientes numa fatura só cobraria um pelo que o outro deve.
  const [selecao, setSelecao]           = useState<Record<number, number[]>>({});
  const [expandidos, setExpandidos]     = useState<number[]>([]);

  // ── Aba "Faturas" ────────────────────────────────────────────────────────
  const [buscaFat, setBuscaFat]         = useState("");
  const [situacao, setSituacao]         = useState<FiltroFaturas["situacao"]>("todas");
  const [pagina, setPagina]             = useState(1);
  const [detalheId, setDetalheId]       = useState<number | null>(null);
  const [baixandoId, setBaixandoId]     = useState<number | null>(null);
  const [estornandoId, setEstornandoId] = useState<number | null>(null);

  const buscaCandAplicada = useDebounce(buscaCand, 400);
  const buscaFatAplicada  = useDebounce(buscaFat, 400);

  // Período pelo VENCIMENTO da fatura; abre no mês corrente inteiro (até o último
  // dia), porque a fatura costuma vencer adiante. Ordem padrão: vencimento
  // crescente — a próxima cobrança a vencer (ou a mais atrasada) no topo.
  const periodo   = usePeriodo("mes", () => setPagina(1), { mesInteiro: true });
  const ordenacao = useOrdenacao<OrdenarPorFaturas>("vencimento", false, () => setPagina(1));

  useEffect(() => { setPagina(1); }, [buscaFatAplicada, situacao]);

  const { clientes, isLoading: carregandoCand } =
    useCandidatasFatura({ busca: buscaCandAplicada });

  const { faturas, total, totalPaginas, isLoading: carregandoFat } =
    useFaturas({
      pagina, tamanhoPagina: 20, busca: buscaFatAplicada, situacao,
      ...periodo.intervalo,
      ordenarPor: ordenacao.ordenarPor,
      decrescente: ordenacao.decrescente,
    });

  const { detalhe, isLoading: carregandoDetalhe } = useFaturaDetalhe(detalheId);

  const {
    gerarFaturas, isGerando,
    baixarFatura, isBaixando,
    estornarFatura, isEstornando,
    baixarPdf, isBaixandoPdf,
  } = useFaturasMutations();

  // ── Seleção ──────────────────────────────────────────────────────────────
  function toggleCliente(clienteId: number, todasIds: number[]) {
    setSelecao((prev) => {
      const atual = prev[clienteId] ?? [];
      const marcarTudo = atual.length !== todasIds.length;
      return { ...prev, [clienteId]: marcarTudo ? todasIds : [] };
    });
  }

  function toggleParcela(clienteId: number, parcelaId: number) {
    setSelecao((prev) => {
      const atual = prev[clienteId] ?? [];
      return {
        ...prev,
        [clienteId]: atual.includes(parcelaId)
          ? atual.filter((x) => x !== parcelaId)
          : [...atual, parcelaId],
      };
    });
  }

  function toggleExpandir(clienteId: number) {
    setExpandidos((prev) =>
      prev.includes(clienteId) ? prev.filter((x) => x !== clienteId) : [...prev, clienteId]);
  }

  const itensSelecionados = Object.entries(selecao)
    .map(([clienteId, parcelaIds]) => ({ clienteId: Number(clienteId), parcelaIds }))
    .filter((i) => i.parcelaIds.length > 0);

  const totalSelecionado = clientes.reduce((acc, c) => {
    const ids = selecao[c.clienteId] ?? [];
    return acc + c.parcelas.filter((p) => ids.includes(p.parcelaId)).reduce((s, p) => s + p.saldo, 0);
  }, 0);

  function handleGerar() {
    gerarFaturas(
      { itens: itensSelecionados, dtVencimento: vencFatura },
      { onSuccess: () => { setSelecao({}); setExpandidos([]); } },
    );
  }

  const podeGerar = itensSelecionados.length > 0 && !!vencFatura && podeIncluir;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Faturas</h1>
        <p className="text-muted-foreground mt-1">
          Agrupe títulos em aberto de um cliente numa cobrança única
        </p>
      </div>

      <Tabs defaultValue="gerar">
        <TabsList>
          <TabsTrigger value="gerar">Gerar fatura</TabsTrigger>
          <TabsTrigger value="lista">Faturas geradas</TabsTrigger>
        </TabsList>

        {/* ── Gerar ── */}
        <TabsContent value="gerar" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row gap-3 items-end justify-between">
                <div className="relative flex-1 min-w-[14rem]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar cliente ou documento..."
                    value={buscaCand}
                    onChange={(e) => setBuscaCand(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="vencFatura" className="text-[10px] text-muted-foreground mb-1">
                    Vencimento da fatura *
                  </Label>
                  <Input
                    id="vencFatura"
                    type="date"
                    value={vencFatura}
                    onChange={(e) => setVencFatura(e.target.value)}
                    className="w-44"
                  />
                </div>
                <Button
                  onClick={handleGerar}
                  disabled={!podeGerar || isGerando}
                  className="gap-2"
                >
                  <Layers className="h-4 w-4" />
                  {isGerando
                    ? "Gerando..."
                    : `Gerar ${itensSelecionados.length || ""} fatura(s)`}
                </Button>
              </div>

              {itensSelecionados.length > 0 && (
                <p className="text-sm text-muted-foreground mt-3">
                  Selecionado: <strong>{brl(totalSelecionado)}</strong> em{" "}
                  {itensSelecionados.reduce((s, i) => s + i.parcelaIds.length, 0)} parcela(s),{" "}
                  {itensSelecionados.length} cliente(s) — uma fatura por cliente.
                </p>
              )}
              {itensSelecionados.length > 0 && !vencFatura && (
                <p className="text-sm text-yellow-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" /> Informe o vencimento da fatura.
                </p>
              )}
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Títulos em aberto ainda não faturados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead className="text-right">Parcelas</TableHead>
                      <TableHead>Mais antigo</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {carregandoCand ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                      </TableRow>
                    ) : clientes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Nenhum título disponível para faturar
                        </TableCell>
                      </TableRow>
                    ) : clientes.map((c) => {
                      const ids       = c.parcelas.map((p) => p.parcelaId);
                      const marcadas  = selecao[c.clienteId] ?? [];
                      const todas     = marcadas.length === ids.length && ids.length > 0;
                      const algumas   = marcadas.length > 0 && !todas;
                      const aberto    = expandidos.includes(c.clienteId);

                      return [
                        <TableRow key={`c-${c.clienteId}`}>
                          <TableCell>
                            <Checkbox
                              checked={todas}
                              onCheckedChange={() => toggleCliente(c.clienteId, ids)}
                              aria-label={`Selecionar todas as parcelas de ${c.clienteNome ?? c.clienteId}`}
                              data-state={algumas ? "indeterminate" : undefined}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm" variant="ghost" className="h-6 w-6 p-0"
                              onClick={() => toggleExpandir(c.clienteId)}
                              title={aberto ? "Recolher" : "Ver as parcelas"}
                            >
                              {aberto
                                ? <ChevronDown className="h-4 w-4" />
                                : <ChevronRightIcon className="h-4 w-4" />}
                            </Button>
                          </TableCell>
                          <TableCell className="font-medium">
                            {c.clienteNome || `Cliente #${c.clienteId}`}
                            {marcadas.length > 0 && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                ({marcadas.length} marcada(s))
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {c.grupoClienteNome || "-"}
                          </TableCell>
                          <TableCell className="text-right">{c.qtdParcelas}</TableCell>
                          <TableCell>{fmt(c.vencimentoMaisAntigo)}</TableCell>
                          <TableCell className="text-right font-medium">{brl(c.total)}</TableCell>
                        </TableRow>,

                        ...(aberto ? c.parcelas.map((p) => (
                          <TableRow key={`p-${p.parcelaId}`} className="bg-muted/30">
                            <TableCell></TableCell>
                            <TableCell>
                              <Checkbox
                                checked={marcadas.includes(p.parcelaId)}
                                onCheckedChange={() => toggleParcela(c.clienteId, p.parcelaId)}
                                aria-label={`Selecionar parcela ${p.nroParcela}`}
                              />
                            </TableCell>
                            <TableCell className="text-sm" colSpan={2}>
                              {p.numDocumento || `Título #${p.tituloId}`} — parcela{" "}
                              {p.nroParcela}/{p.qtdParcelas}
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              {p.diasAtraso > 0 && (
                                <Badge variant="outline" className="text-red-500 border-red-500/40">
                                  {p.diasAtraso}d
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{fmt(p.dtVencimento)}</TableCell>
                            <TableCell className="text-right text-sm">{brl(p.saldo)}</TableCell>
                          </TableRow>
                        )) : []),
                      ];
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Faturas geradas ── */}
        <TabsContent value="lista" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <CardTitle>Faturas</CardTitle>
                  <span className="text-sm text-muted-foreground">({total})</span>
                </div>
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="relative w-56">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar cliente..."
                      value={buscaFat}
                      onChange={(e) => setBuscaFat(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <PeriodoFiltro estado={periodo} rotuloData="Vencimento" />
                  <Select
                    value={situacao}
                    onValueChange={(v) => setSituacao(v as FiltroFaturas["situacao"])}
                  >
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todas">Todas</SelectItem>
                      <SelectItem value="aberta">Abertas</SelectItem>
                      <SelectItem value="vencida">Vencidas</SelectItem>
                      <SelectItem value="recebida">Recebidas</SelectItem>
                      <SelectItem value="cancelada">Canceladas</SelectItem>
                    </SelectContent>
                  </Select>
                  {(buscaFat || situacao !== "todas" || periodo.periodo !== "mes") && (
                    <Button
                      variant="ghost" size="sm" className="gap-1"
                      onClick={() => { setBuscaFat(""); setSituacao("todas"); periodo.setPeriodo("mes"); }}
                    >
                      <X className="h-4 w-4" /> Limpar
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Fatura</CabecalhoOrdenavel>
                      <TableHead>Cliente</TableHead>
                      <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel>
                      <CabecalhoOrdenavel coluna="vencimento" ordenacao={ordenacao} decrescentePadrao={false}>Vencimento</CabecalhoOrdenavel>
                      <TableHead className="text-right">Títulos</TableHead>
                      <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Valor</CabecalhoOrdenavel>
                      <TableHead>Situação</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {carregandoFat ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                      </TableRow>
                    ) : faturas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          Nenhuma fatura encontrada
                        </TableCell>
                      </TableRow>
                    ) : faturas.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell className="font-medium">#{f.id}</TableCell>
                        <TableCell>{f.clienteNome || `Cliente #${f.clienteId}`}</TableCell>
                        <TableCell>{fmt(f.dtEmissao)}</TableCell>
                        <TableCell>
                          {fmt(f.dtVencimento)}
                          {f.diasAtraso > 0 && (
                            <span className="ml-2 text-xs text-red-500">{f.diasAtraso}d</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{f.qtdTitulos}</TableCell>
                        <TableCell className="text-right font-medium">{brl(f.valorTotal)}</TableCell>
                        <TableCell>
                          <Badge className={situacaoCor[f.situacao] ?? ""}>{f.situacao}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setDetalheId(f.id)}
                              title="Ver os títulos agrupados"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => baixarPdf(f.id)}
                              disabled={isBaixandoPdf}
                              title="Imprimir / baixar PDF"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            {!f.isPago && !f.isCancelado && podeEditar && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => setBaixandoId(f.id)}
                                disabled={isBaixando}
                                title="Receber a fatura (quita os títulos agrupados)"
                              >
                                <CheckCheck className="h-4 w-4" />
                              </Button>
                            )}
                            {!f.isPago && !f.isCancelado && podeExcluir && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => setEstornandoId(f.id)}
                                disabled={isEstornando}
                                title="Estornar a fatura (desfaz o agrupamento)"
                              >
                                <RotateCcw className="h-4 w-4 text-orange-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPaginas > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Página {pagina} de {totalPaginas} ({total} faturas)
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPagina((p) => p - 1)} disabled={pagina <= 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Detalhe: os títulos que a fatura agrupa ── */}
      <Sheet open={detalheId !== null} onOpenChange={(o) => !o && setDetalheId(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Fatura #{detalheId}</SheetTitle>
          </SheetHeader>

          {carregandoDetalhe ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : !detalhe ? (
            <p className="text-sm text-muted-foreground">Fatura não encontrada.</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Cliente</p>
                  <p className="font-medium">
                    {detalhe.fatura.clienteNome || `Cliente #${detalhe.fatura.clienteId}`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Situação</p>
                  <Badge className={situacaoCor[detalhe.fatura.situacao] ?? ""}>
                    {detalhe.fatura.situacao}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Emissão</p>
                  <p>{fmt(detalhe.fatura.dtEmissao)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Vencimento</p>
                  <p>{fmt(detalhe.fatura.dtVencimento)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Valor</p>
                  <p className="font-semibold">{brl(detalhe.fatura.valorTotal)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Saldo</p>
                  <p className="font-semibold">{brl(detalhe.fatura.saldo)}</p>
                </div>
              </div>

              <div>
                <p className="font-medium mb-2">
                  Títulos agrupados ({detalhe.titulos.length})
                </p>
                <p className="text-xs text-muted-foreground mb-2">
                  A dívida continua nestes títulos — a fatura só concentra a cobrança.
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Documento</TableHead>
                      <TableHead>Parcela</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detalhe.titulos.map((t) => (
                      <TableRow key={t.parcelaId}>
                        <TableCell>{t.numDocumento || `#${t.tituloId}`}</TableCell>
                        <TableCell>{t.nroParcela}/{t.qtdParcelas}</TableCell>
                        <TableCell>{fmt(t.dtVencimento)}</TableCell>
                        <TableCell className="text-right">{brl(t.saldo)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Button
                variant="outline" className="gap-2"
                onClick={() => baixarPdf(detalhe.fatura.id)}
                disabled={isBaixandoPdf}
              >
                <Printer className="h-4 w-4" /> Baixar PDF
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Receber a fatura ── */}
      <DeleteConfirmDialog
        open={baixandoId !== null}
        onOpenChange={(o) => !o && setBaixandoId(null)}
        onConfirm={() => {
          if (baixandoId !== null)
            baixarFatura(
              { id: baixandoId, req: { dtPagamento: hojeIso() } },
              { onSuccess: () => setBaixandoId(null) },
            );
        }}
        title="Receber a fatura"
        confirmLabel="Receber"
        tom="default"
        description={
          "Isto quita TODOS os títulos agrupados nesta fatura, na data de hoje. " +
          "A dívida está nos títulos — é por eles que a baixa acontece."
        }
      />

      {/* ── Estornar a fatura ── */}
      <DeleteConfirmDialog
        open={estornandoId !== null}
        onOpenChange={(o) => !o && setEstornandoId(null)}
        onConfirm={() => {
          if (estornandoId !== null)
            estornarFatura(estornandoId, { onSuccess: () => setEstornandoId(null) });
        }}
        title="Estornar a fatura"
        confirmLabel="Estornar"
        description={
          "O agrupamento é desfeito e os títulos voltam a ser cobrados " +
          "individualmente. A fatura fica cancelada, preservando a trilha."
        }
      />
    </div>
  );
};

export default Faturas;
